import { useMemo } from "react";
import ReactTooltip from "react-tooltip";
import { LazyLoadRowProps } from "../shared-components/LazyLoadTable";
import { ReportRow } from "./Table";
import ReportStatus from "./ReportStatus";
import ReportStepsModal from "./ReportStepsModal";
import {
  ArrowPathIcon,
  ArrowPathRoundedSquareIcon,
  ClipboardIcon,
} from "@heroicons/react/24/outline";
import MisconductCancelButton from "./MisconductCancelButton";
import TableReportDateWithFormat from "../shared-components/TableReportDateWithFormat";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import useNavigateWithState from "./useNavigateWithState";
import {
  WORKFLOW_STATUS_ENUM,
  WORKFLOW_STEP_STATUS_ENUM,
  WorkflowRunEx,
  WorkflowRunRowDataResponse,
  isWorkflowRunVisuallyLockedWithListRow,
} from "../api/Workflows";
import { formattedISODate } from "../utils";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import NotLoadedYet from "../shared-components/NotLoadedYet";
import { useGetWorkflowRunByUUID } from "../api/WorkflowApi";
import { useQueryClient } from "react-query";
import {
  ReportEntryStyle,
  ReportRowItem,
} from "./helper-components/TableModels";
import { ReportSummaryItemApi } from "../api/Reports";

const ClientQALazyRowV2 = ({
  parentRow,
  index,
}: LazyLoadRowProps<WorkflowRunEx>): JSX.Element => {
  const { navigateWithState } = useNavigateWithState();
  const client = useQueryClient();
  const { data: workflowRun } = useGetWorkflowRunByUUID(parentRow.external_id);
  const permissions = usePermissionValidator();

  const getStatusText = (
    workflowRun: WorkflowRunRowDataResponse | undefined
  ): string => {
    if (workflowRun?.status === WORKFLOW_STATUS_ENUM.FAILED) {
      return "ERROR";
    }

    const clientQAStep = workflowRun?.steps_in_progress?.find(
      (step: string) => step === WORKFLOW_STEP_STATUS_ENUM.ClientQA
    );
    return clientQAStep || "Loading...";
  };

  function WorkflowRunExToRow(
    wrx: WorkflowRunEx
  ): ReportRowItem<ReportSummaryItemApi> {
    const rowItem = {
      report_uuid: wrx.payload.report_uuid,
      report: undefined,
      isLocked: isWorkflowRunVisuallyLockedWithListRow(undefined, wrx),
      data: [
        {
          text: wrx.payload.full_name || "?",
          style: ReportEntryStyle.COMPONENT,
          component: (
            <div className="flex">
              <div>
                <span className="font-semibold">
                  {wrx.payload.full_name || "?"}
                </span>
                <small className="block font-normal">
                  {wrx.payload.company_name || "?"}
                </small>
              </div>
            </div>
          ),
        },
        {
          text: "status",
          style: ReportEntryStyle.COMPONENT,
          component: <NotLoadedYet />,
        },
        {
          text: "check type",
          style: ReportEntryStyle.COMPONENT,
          component: (
            <div>
              <NotLoadedYet className="h-4 w-12 block" />
              <br />
              <NotLoadedYet className="h-4 w-16 block" />
            </div>
          ),
        },
        {
          text: "date added",
          style: ReportEntryStyle.COMPONENT,
          component: <NotLoadedYet />,
        },
        {
          text: "due date",
          style: ReportEntryStyle.COMPONENT,
          component: <NotLoadedYet />,
        },
        {
          text: "request uuid",
          style: ReportEntryStyle.COMPONENT,
          component: <NotLoadedYet className="h-6 w-20" />,
        },
        {
          text: "custom id",
          style: ReportEntryStyle.COMPONENT,
          component: <NotLoadedYet />,
        },
      ],
    };
    if (permissions.canCancelMisconduct) {
      rowItem.data.push({
        text: "cancel",
        style: ReportEntryStyle.COMPONENT,
        component: (
          <MisconductCancelButton reportUUID={wrx.payload.report_uuid} />
        ),
      });
    }
    return rowItem;
  }

  function WorkflowRunToRow(
    wr: WorkflowRunRowDataResponse
  ): ReportRowItem<ReportSummaryItemApi> {
    const rowItem: ReportRowItem<ReportSummaryItemApi> = {
      report_uuid: workflowRun?.report_uuid || "",
      report: undefined,
      isLocked: isWorkflowRunVisuallyLockedWithListRow(workflowRun, parentRow),
      data: [
        {
          text: workflowRun?.full_name || "",
          style: ReportEntryStyle.COMPONENT,
          component: (
            <div className="flex">
              <div>
                {permissions.canClientQaEdit ? (
                  <span
                    onClick={async () => {
                      await client.refetchQueries({
                        queryKey: ["workflowRun"],
                      });
                      navigateWithState(workflowRun?.report_uuid || "", "cqa");
                    }}
                    className="font-semibold underline cursor-pointer"
                  >
                    {workflowRun?.full_name || ""}
                  </span>
                ) : (
                  <span className="font-semibold">
                    {workflowRun?.full_name || ""}
                  </span>
                )}
                <small className="block font-normal">
                  {workflowRun?.company_name || ""}
                </small>
              </div>
              <div>
                {(workflowRun?.rerun_from_parent_id ||
                  workflowRun?.has_rerun) && (
                  <div>
                    <ArrowPathIcon className="w-5 ml-2 text-blue-600" />
                  </div>
                )}
              </div>
            </div>
          ),
        },
        {
          text: workflowRun?.status || "",
          style: ReportEntryStyle.COMPONENT,
          component: (
            <>
              <ReportStatus
                stepStatusName={getStatusText(workflowRun)}
                workflowRunUUID={workflowRun?.external_id}
                failed={workflowRun?.status === WORKFLOW_STATUS_ENUM.FAILED}
                allowResumeOnClick={true}
              />
              <ReportStepsModal
                workflowRunUUID={
                  workflowRun?.external_id || parentRow.external_id || ""
                }
              />
            </>
          ),
          customClass: "flex-1 w-4",
        },
        {
          component: (
            <div className="flex flex-col">
              <span className="border-b-2">
                {workflowRun?.blueprint_name || ""}
              </span>{" "}
              <small className="block">
                {workflowRun?.behavior_kit_name ?? "General"}
              </small>
            </div>
          ),
          text:
            (workflowRun?.blueprint_name || "") +
            " " +
            (workflowRun?.behavior_kit_name ?? "General"),
          style: ReportEntryStyle.COMPONENT,
        },
        {
          text: workflowRun?.created_at
            ? formattedISODate(workflowRun.created_at)
            : "",
          component: workflowRun?.recur ? (
            <>
              <TableReportDateWithFormat
                date={workflowRun?.report_created_at || ""}
                updated={false}
                unique_id={workflowRun?.report_uuid || ""}
              />
              <span
                data-tip="Employee Screening"
                data-tooltip-id={`recur-tip-${workflowRun?.report_uuid}`}
                className="cursor-pointer"
              >
                <ArrowPathRoundedSquareIcon className="w-5 text-gray-500" />
              </span>
              <ReactTooltip id={`recur-tip-${workflowRun?.report_uuid}`} />
            </>
          ) : (
            <TableReportDateWithFormat
              date={workflowRun?.report_created_at || ""}
              updated={false}
            />
          ),
          style: ReportEntryStyle.COMPONENT,
          customClass: "flex-1 whitespace-pre",
        },
        {
          text: workflowRun?.due_date
            ? formattedISODate(workflowRun.due_date)
            : "",
          component: (
            <TableReportDateWithFormat
              date={workflowRun?.due_date || ""}
              updated={workflowRun?.due_date_updated}
            />
          ),
          style: ReportEntryStyle.COMPONENT,
          customClass: "flex-1 whitespace-pre",
        },
        {
          text: workflowRun?.report_uuid || "",
          style: ReportEntryStyle.COMPONENT,
          component: (
            <div className="flex gap-1">
              <ClipboardIcon
                onClick={() => {
                  navigator.clipboard.writeText(workflowRun?.report_uuid || "");
                }}
                className="w-4 cursor-pointer"
              ></ClipboardIcon>
              <span className={"w-16 h-[30px] truncate"}>
                {workflowRun?.report_uuid || ""}
              </span>
            </div>
          ),
        },
        {
          text: `${workflowRun?.client_report_id || ""}`,
          style: ReportEntryStyle.TEXT,
        },
      ],
    };
    if (permissions.canCancelMisconduct) {
      rowItem.data.push({
        text: "Cancel",
        style: ReportEntryStyle.COMPONENT,
        component: (
          <MisconductCancelButton reportUUID={workflowRun?.report_uuid || ""} />
        ),
      });
    }
    return rowItem;
  }

  const row = useMemo(() => {
    if (workflowRun) {
      return WorkflowRunToRow(workflowRun);
    }
    return WorkflowRunExToRow(parentRow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowRun, parentRow]);

  if (!row) {
    return <LoadingSpinner />;
  }
  return <ReportRow reportRow={row} index={index} />;
};

export default ClientQALazyRowV2;
